import { inject,Injectable } from "@angular/core";

import { AlignmentType, Document, HeadingLevel, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } from "docx";

import { LoggerService } from "@core/services/logger.service";

import { MarkdownService } from "./markdown.service";

@Injectable({
  providedIn: "root"
})
export class DocxExportService {
  private readonly _markdownService = inject(MarkdownService);
  private readonly _logger = inject(LoggerService);

  /**
   * Export document as DOCX format
   */
  async exportToDocx(markdown: string, title: string = "Document"): Promise<Blob> {
    try {
      const html = this._markdownService.markdownToHtml(markdown);

      const docxSections = this._htmlToDocxSections(html, title);

      const doc = new Document({
        numbering: {
          config: [
            {
              reference: "bullet",
              levels: [
                {
                  level: 0,
                  format: "bullet",
                  text: "•",
                  alignment: AlignmentType.LEFT,
                  style: { paragraph: { indent: { left: 720, hanging: 360 } } }
                },
                {
                  level: 1,
                  format: "bullet",
                  text: "◦",
                  alignment: AlignmentType.LEFT,
                  style: { paragraph: { indent: { left: 1440, hanging: 360 } } }
                },
                {
                  level: 2,
                  format: "bullet",
                  text: "▪",
                  alignment: AlignmentType.LEFT,
                  style: { paragraph: { indent: { left: 2160, hanging: 360 } } }
                }
              ]
            },
            {
              reference: "number",
              levels: [
                {
                  level: 0,
                  format: "decimal",
                  text: "%1.",
                  alignment: AlignmentType.LEFT,
                  style: { paragraph: { indent: { left: 720, hanging: 360 } } }
                },
                {
                  level: 1,
                  format: "lowerLetter",
                  text: "%2.",
                  alignment: AlignmentType.LEFT,
                  style: { paragraph: { indent: { left: 1440, hanging: 360 } } }
                },
                {
                  level: 2,
                  format: "lowerRoman",
                  text: "%3.",
                  alignment: AlignmentType.LEFT,
                  style: { paragraph: { indent: { left: 2160, hanging: 360 } } }
                }
              ]
            }
          ]
        },
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: 1440,
                  right: 1440,
                  bottom: 1440,
                  left: 1440,
                  header: 720,
                  footer: 720,
                  gutter: 0
                }
              }
            },
            children: docxSections
          }
        ]
      });

      return await Packer.toBlob(doc);
    } catch (error) {
      this._logger.error("DOCX export failed", error as any);
      throw new Error("Failed to generate DOCX document");
    }
  }

  private _htmlToDocxSections(html: string, title?: string): any[] {
    const sections: any[] = [];
    if (title) {
      sections.push(
        new Paragraph({
          text: title,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 300 }
        })
      );
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const body = doc.body || doc.documentElement;
    this._convertElementToDocx(body, sections, 0);

    return sections;
  }

  private _convertElementToDocx(element: Element, sections: any[], listIndentLevel: number): void {
    for (const node of Array.from(element.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        if (text.trim()) {
          sections.push(
            new Paragraph({
              children: [new TextRun(text.trim())]
            })
          );
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        const tagName = el.tagName.toLowerCase();

        switch (tagName) {
          case "h1":
            sections.push(
              new Paragraph({
                text: el.textContent || "",
                heading: HeadingLevel.HEADING_1,
                spacing: { after: 300 }
              })
            );
            break;
          case "h2":
            sections.push(
              new Paragraph({
                text: el.textContent || "",
                heading: HeadingLevel.HEADING_2,
                spacing: { after: 250 }
              })
            );
            break;
          case "h3":
            sections.push(
              new Paragraph({
                text: el.textContent || "",
                heading: HeadingLevel.HEADING_3,
                spacing: { after: 200 }
              })
            );
            break;
          case "h4":
          case "h5":
          case "h6":
            sections.push(
              new Paragraph({
                text: el.textContent || "",
                heading: HeadingLevel.HEADING_4,
                spacing: { after: 150 }
              })
            );
            break;
          case "p":
            sections.push(this._createParagraphFromElement(el));
            break;
          case "ul":
            this._convertListToDocx(el, false, sections, listIndentLevel + 1);
            break;
          case "ol":
            this._convertListToDocx(el, true, sections, listIndentLevel + 1);
            break;
          case "table":
            sections.push(this._createTableFromElement(el));
            break;
          case "pre":
            sections.push(this._createCodeBlockFromElement(el));
            break;
          case "blockquote":
            sections.push(this._createBlockquoteFromElement(el));
            break;
          case "br":
            sections.push(new Paragraph(""));
            break;
          default:
            // For other elements, process their children
            this._convertElementToDocx(el, sections, listIndentLevel);
            break;
        }
      }
    }
  }

  private _convertListToDocx(element: Element, isOrdered: boolean, sections: any[], level: number): void {
    const items = Array.from(element.children).filter((n) => n.tagName?.toLowerCase() === "li") as Element[];

    for (const item of items) {
      // Extract text part of the list item (excluding nested lists)
      const cloned = item.cloneNode(true) as Element;
      for (const nested of Array.from(cloned.querySelectorAll("ul,ol"))) {
        nested.remove();
      }
      const runs = this._createRunsFromElement(cloned);
      sections.push(
        new Paragraph({
          children: runs.length ? runs : [new TextRun(cloned.textContent || "")],
          numbering: { reference: isOrdered ? "number" : "bullet", level: Math.max(0, level - 1) }
        })
      );

      // Recurse into nested lists inside the item
      const nestedUls = Array.from(item.querySelectorAll(":scope > ul"));
      const nestedOls = Array.from(item.querySelectorAll(":scope > ol"));
      for (const ul of nestedUls) this._convertListToDocx(ul, false, sections, level + 1);
      for (const ol of nestedOls) this._convertListToDocx(ol, true, sections, level + 1);
    }
  }

  private _createParagraphFromElement(element: Element): Paragraph {
    const runs = this._createRunsFromElement(element);
    return new Paragraph({
      children: runs.length ? runs : [new TextRun(element.textContent || "")],
      alignment: AlignmentType.LEFT
    });
  }

  private _createTableFromElement(element: Element): Table {
    const rows: TableRow[] = [];
    const tableRows = element.querySelectorAll("tr");

    tableRows.forEach((tr) => {
      const cells: TableCell[] = [];
      const tableCells = tr.querySelectorAll("td, th");

      tableCells.forEach((td) => {
        const text = td.textContent || "";
        const isHeader = td.tagName.toLowerCase() === "th";

        cells.push(
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: text,
                    bold: isHeader
                  })
                ]
              })
            ]
          })
        );
      });

      if (cells.length > 0) {
        rows.push(
          new TableRow({
            children: cells
          })
        );
      }
    });

    return new Table({
      rows: rows,
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      }
    });
  }

  private _createCodeBlockFromElement(element: Element): Paragraph {
    const code = element.querySelector("code") || element;
    const text = code.textContent || "";

    return new Paragraph({
      children: [
        new TextRun({
          text: text,
          font: "Courier New",
          size: 20 // 10pt
        })
      ],
      shading: {
        type: "pct15",
        color: "F8F8F8"
      }
    });
  }

  private _createBlockquoteFromElement(element: Element): Paragraph {
    const text = element.textContent || "";

    return new Paragraph({
      children: [
        new TextRun({
          text: text,
          italics: true
        })
      ],
      indent: { left: 720 }, // 0.5 inch indent
      shading: {
        type: "pct10",
        color: "F0F0F0"
      }
    });
  }

  private _createRunsFromElement(element: Element): TextRun[] {
    const runs: TextRun[] = [];

    const walk = (node: Node, marks: { bold?: boolean; italics?: boolean; code?: boolean; href?: string }) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent || "").replace(/\s+/g, " ");
        if (text) {
          runs.push(
            new TextRun({
              text,
              bold: !!marks.bold,
              italics: !!marks.italics,
              font: marks.code ? "Courier New" : undefined,
              underline: marks.href ? {} : undefined
            })
          );
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      const nextMarks = { ...marks };
      if (tag === "strong" || tag === "b") nextMarks.bold = true;
      if (tag === "em" || tag === "i") nextMarks.italics = true;
      if (tag === "code") nextMarks.code = true;
      if (tag === "a") nextMarks.href = el.getAttribute("href") || undefined;
      if (tag === "br") {
        runs.push(new TextRun({ text: "\n" }));
        return;
      }
      for (const child of Array.from(el.childNodes)) {
        walk(child, nextMarks);
      }
    };

    for (const child of Array.from(element.childNodes)) {
      walk(child, {});
    }

    return runs;
  }
}
